import React, { useContext, useEffect, useState } from "react";

import { ethers } from "ethers";

import Logo from "./shared/Logo"
import Connector from "./Connector";
import PreSaleDApp from "./PreSaleDApp";
import PubSaleDApp from "./PubSaleDApp";
import GenericMessage from "./shared/GenericMessage";

import { MetaMaskContext } from '../context/mm-context'
import LoadingSpinner from "./shared/LoadingSpinner";

import merkleProofs from '../data/merkleProof.json'
import Refresher from "./Refresher";

function Home() {

    // bring in the site wide state
    const ctx = useContext(MetaMaskContext);

    // set some state
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [contractData, setContractData] = useState({
        isContractData: false,
        name: '',
        isSaleActive: false,
        isPreSaleActive: false,
        remainingPublicSupply: 0,
        totalSupply: 0,
        isWhitelisted: false,
        proof: null,
        maxMintAmountPerTx: 0,
        maxPreSalePerWallet: 0,
        maxPubSalePerWallet: 0,
        preSaleCost: 0,
        pubSaleCost: 0,
        mintedByWhitelist: 0,
        mintedInPublicSale: 0,
        isPreSale: false,
        isPubSale: false,
        isSoldOut: false,
        isActive: false,
        soldOutAddr: null,
    });
    
    // extract all variables from the contract state
    const {
        name,
        isPreSale,
        isPubSale,
        isSoldOut,
        isActive,
        soldOutAddr,
    } = contractData;


    // read the contract
    const getContractData = async (setLoadingState = true) => {

        // show loading spinner if asked to
        setLoadingState && setIsFetchingData(true);
        
        // get the name of the NFT
        const name = await ctx.ethContract.name();

        // check contract getter functions
        const isSaleActive = await ctx.ethContract.isSaleActive();
        if (!isSaleActive) {
            setContractData((prevState) => ({
                ...prevState,
                isContractData: true,
                isSaleActive,
                isPreSale: false,
                isPubSale: false,
                isSoldOut: false,
                isActive: false,
                name,
           }));
            setIsFetchingData(false);
            return;
        }

        const remainingPublicSupply = await ctx.ethContract.getRemainingPublicSupply();
        if (parseInt(remainingPublicSupply) <= 0) {
            setContractData((prevState) => ({
                ...prevState,
                isContractData: true,
                isSaleActive,
                isSoldOut: true,
                isPreSale: false,
                isPubSale: false,
                isActive: true,
                name,
           }));
            setIsFetchingData(false);
            return;
        }
        
        const isPreSaleActive = await ctx.ethContract.isPreSaleActive();
        const minters = await ctx.ethContract.minters(ctx.ethAddress);
        
        if (isPreSaleActive) {
            
            const merkleProof = merkleProofs.find(m => (m.address.toLowerCase() === ctx.ethAddress.toLowerCase()));
            const proof = typeof (merkleProof) !== 'undefined' ? merkleProof.proof.split(",") : ["0x0000000000000000000000000000000000000000000000000000000000000000"];
            const isWhitelisted = await ctx.ethContract.isWhitelisted(proof, ctx.ethAddress);
            const preSaleCost = await ctx.ethContract.preSaleCost();
            const maxPreSalePerWallet = await ctx.ethContract.maxPreSalePerWallet();

            setContractData((prevState) => ({
                ...prevState,
                isContractData: true,
                name,
                isSaleActive,
                isPreSaleActive,
                remainingPublicSupply: parseInt(remainingPublicSupply),
                isWhitelisted,
                proof,
                maxPreSalePerWallet: parseInt(maxPreSalePerWallet),
                preSaleCost: parseInt(preSaleCost),
                preSaleCostEth: ethers.utils.formatEther(BigInt(preSaleCost)),
                mintedByWhitelist: parseInt(minters.hasMintedByWhitelist),
                isPubSale: !isPreSaleActive && isSaleActive && remainingPublicSupply > 0,
                isPreSale: isPreSaleActive && isSaleActive && remainingPublicSupply > 0,
                isSoldOut: remainingPublicSupply === 0,
                isActive: isSaleActive,
            }));
                
        } else {

            const pubSaleCost = await ctx.ethContract.pubSaleCost();
            const maxMintAmountPerTx = await ctx.ethContract.maxMintAmountPerTx();
            const maxPubSalePerWallet = await ctx.ethContract.maxPubSalePerWallet();
            const totalSupply = await ctx.ethContract.totalSupply();

            setContractData((prevState) => ({
                ...prevState,
                isContractData: true,
                name,
                isSaleActive,
                isPreSaleActive,
                remainingPublicSupply: parseInt(remainingPublicSupply),
                totalSupply: parseInt(totalSupply),
                maxMintAmountPerTx: parseInt(maxMintAmountPerTx),
                maxPubSalePerWallet: parseInt(maxPubSalePerWallet),
                pubSaleCost: parseInt(pubSaleCost),
                pubSaleCostEth: ethers.utils.formatEther(BigInt(pubSaleCost)),
                isPreSale: isPreSaleActive && isSaleActive && remainingPublicSupply > 0,
                isPubSale: !isPreSaleActive && isSaleActive && remainingPublicSupply > 0,
                isSoldOut: remainingPublicSupply === 0,
                isActive: isSaleActive,
                mintedInPublicSale: parseInt(minters.hasMintedInPublicSale),
            }));
                
        }

        setLoadingState && setIsFetchingData(false);

    }

    // get the contact data once connected
    useEffect(() => {
        ctx.isConnected && getContractData()
    }, [ctx.isConnected])

    return (
        <>
            
            {ctx.isConnected && (
                <div className="bg-tota-blur mb-4 text-tota text-center p-1 w-100 fw-bold" style={{ fontSize: "10px", fontWeight: "400", letterSpacing: "1px", top: "0" }}>
                    connected to: {ctx.ensAddress || "[" + ctx.ethAddress.slice(0, 6) + "..." + ctx.ethAddress.slice(-4) + "]"}
                </div>
            )}

            
            <main className="container text-center mb-5">
                <Logo className="img-fluid mt-3 mb-5" alt="logo" src="./images/logo.png" style={{ maxWidth: "450px" }} />
                <Connector />
                {/* {!isFetchingData && ctx.isConnected && 
                    <Refresher setContractData={setContractData} show={false} />
                } */}
                {isFetchingData && ctx.isConnected && 
                    <LoadingSpinner text="LOADING" addClasses="text-tota" />
                }
                {!isFetchingData && ctx.isConnected && isPreSale && 
                    <PreSaleDApp data={contractData} getContractData={getContractData} />
                }
                {!isFetchingData && ctx.isConnected && isPubSale && 
                    <PubSaleDApp data={contractData} getContractData={getContractData} />
                }
                {!isFetchingData && ctx.isConnected && isSoldOut && 
                    <GenericMessage>
                        {name} NFTs have sold out<br />If you still want to purchase our NFT, plese visit <a href={ctx.network.openSea + "collection/" + name.toLowerCase().replace(/ /g, "-")} rel="noreferrer" target="_blank">OpenSea</a> to browse the open market.
                    </GenericMessage>
                }
                {!isFetchingData && ctx.isConnected && !isActive && !isSoldOut && 
                    <GenericMessage>
                        Minting is not currently active.<br /> Please try again later.
                    </GenericMessage>
                }
            </main>

            {ctx.isConnected && (
                <footer className="footer text-center fs-6 mt-auto py-2 bg-tota-blur w-100">
                    <div className="container">
                        <span style={{ fontSize: "12px", fontWeight: "400", letterSpacing: "1px" }} className="text-tota">contract address: [<a target="_blank" rel="noreferrer" data-bs-toggle="tooltip" data-bs-placement="top" title={ctx.contractAddress} alt={ctx.contractAddress} href={ctx.network.etherScan + "address/" + ctx.contractAddress}>{ctx.contractAddress.slice(0, 6) + "..." + ctx.contractAddress.slice(-4)}</a>]</span>
                    </div>
                </footer>
            )}
        </>
    );
}

export default Home;
